import { ChangeDetectionStrategy, Component, ChangeDetectorRef, AfterViewInit, OnInit, OnDestroy, Input, Optional } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BuilderScreenBetaService } from '../services/builder-screen.service';
import { GridLayoutService } from '../services/grid-layout.service';
import { SaveComponentsService } from '../services/save-components.service';
import { TabControlService } from '../services/tab-control.service';
import { MasterDetailService, MasterDetailState } from '../services/master-detail.service';
import { DataBindingService } from '../services/data-binding.service';
import { DataSyncService } from '../services/data-sync.service';
import { NumberFormatterService, NumberFormatConfig } from '../services/number-formatter.service';
import { BuilderNode, GridPosition, BuilderComponentType, ButtonProps, ContextFieldConfig } from '../interfaces/builder-screen.models';
import { Observable, combineLatest, map, take, Subscription, debounceTime } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { FlowModalComponent } from 'src/app/forms/flow-action/flowaction.component';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { HTTPService } from 'src/app/services/http.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { EventScriptService } from '../services/event-script.service';
import { NzIconService } from 'ng-zorro-antd/icon';
import { FormValidationService } from '../services/form-validation.service';
import { FlowCompletionNotifyService } from '../services/flow-completion-notify.service';

@Component({
  selector: 'app-builder-canvas-beta',
  templateUrl: './canvas.component.html',
  styleUrls: ['./canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CanvasBetaComponent implements OnInit, AfterViewInit, OnDestroy {

  /**
   * Modo de visualização do canvas:
   * - 'edit': Modo de edição com drag-drop, seleção, remoção (padrão no Builder)
   * - 'view': Modo de visualização para launchers, sem ferramentas de edição
   */
  @Input() viewMode: 'edit' | 'view' = 'edit';

  public readonly gridPositions$: Observable<Map<string, GridPosition>>;
  public readonly gridHeight$: Observable<number>;
  public showGridOverlay = false;
  
  /**
   * FormGroup vazio para evitar erros de FormControl em campos ng-zorro
   * Alguns componentes do ng-zorro tentam se auto-conectar a um FormGroup parent
   */
  public dummyFormGroup!: FormGroup;
  
  /**
   *  Subscription para Master-Detail
   */
  private masterDetailSubscription?: Subscription;

  /**
   *  Subscription para DataBinding (atualização de formulários)
   */
  private dataBindingSubscription?: Subscription;

  /**
   *  NOVO: Panel Master-Detail ativo
   */
  public activeMasterDetailPanel: { formPanelId: string } | null = null;

  private flipActivePanels = new Set<string>();
  private drawerActivePanels = new Set<string>();
  public flipActiveGridIds = new Set<string>();
  public panelsWithFlipActiveGrid = new Set<string>();

  private _nonInlinePanelIds = new Set<string>();
  public samePanelNonInlineGridParents = new Set<string>();

  /**
   *  NOVO: Dados da linha atualmente selecionada
   *  Público para acesso no template (context strip)
   */
  public currentSelectedRowData: any = null;
  private currentFormPanelId: string | null = null; // Legacy: single panel ID
  private currentFormPanelIds: string[] = []; // Multi-panel support
  
  /**
   *  Flag para prevenir loop infinito na sincronização
   */
  private isUpdatingFromGlobalData = false;

  /**
   * Timestamp da última atualização de formulário (debounce)
   */
  private _lastFormUpdateTimestamp = 0;

  /**
   *  Flag para evitar atualizar formulários quando mudança vem do próprio formulário
   */
  private isUpdatingFromFormChange = false;

  /**
   *  Cache de valores resolvidos para campos de pesquisa (search/querySelect)
   * Chave: "dataPath:código" → Valor: "código - descrição"
   */
  private searchDisplayCache = new Map<string, string>();

  /**
   * Verifica se está em modo de edição
   */
  get isEditMode(): boolean {
    return this.viewMode === 'edit';
  }

  /**
   * Verifica se está em modo de visualização
   */
  get isViewMode(): boolean {
    return this.viewMode === 'view';
  }

  /**
   *  NOVO: Verifica se os componentes devem estar em modo preview
   * - true em modo 'edit' (apenas visualização, sem binding)
   * - false em modo 'view' (produção, com binding ativo)
   */
  get isPreviewMode(): boolean {
    return this.viewMode === 'edit';
  }

  constructor(
    public readonly builderService: BuilderScreenBetaService,
    private readonly gridLayoutService: GridLayoutService,
    private readonly saveService: SaveComponentsService,
    private readonly tabControlService: TabControlService,
    private readonly masterDetailService: MasterDetailService,
    private readonly dataBindingService: DataBindingService,
    private readonly dataSyncService: DataSyncService,
    private readonly eventScriptService: EventScriptService,
    private readonly formValidationService: FormValidationService,
    private readonly numberFormatterService: NumberFormatterService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialogService: DialogService,
    private readonly uas: UserAuthenticationServices,
    private readonly message: NzMessageService,
    private readonly modal: NzModalService,
    private readonly fb: FormBuilder,
    private readonly nzIconService: NzIconService,
    private readonly http: HTTPService,
    @Optional() private readonly flowCompletionNotify: FlowCompletionNotifyService
  ) {
    // Calcula posições da grade de forma reativa
    this.gridPositions$ = this.builderService.nodes$.pipe(
      map(nodes => {
        this.refreshNonInlinePanelIds();
        return this.gridLayoutService.calculateGridPosition([...nodes]);
      })
    );

    // Calcula altura dinâmica da grade
    this.gridHeight$ = this.builderService.nodes$.pipe(
      map(nodes => this.gridLayoutService.calculateGridHeight([...nodes]))
    );
    
    // ✅ Criar FormGroup vazio para evitar erro de FormControl
    // Alguns componentes ng-zorro (nz-input, nz-input-number) tentam se auto-conectar
    // a um FormGroup parent. Este FormGroup vazio previne o erro "_rawValidators of null"
    this.dummyFormGroup = this.fb.group({});
  }


  trackById(index: number, node: BuilderNode): string {
    return node.id;  
  }

  /**
   *  NOVO: TrackBy que inclui a ordem do painel para forçar re-renderização quando ordem muda
   */
  trackByPanelOrder(index: number, node: BuilderNode): string {
    const order = (node.props as any)?.order ?? index;
    return `${node.id}-${order}`;
  }

  getTabItems(node: BuilderNode): ReadonlyArray<{ id: string; name: string }>
  {
    const items = (node?.props as any)?.items;
    return Array.isArray(items) ? items : [];
  }

  // ── Barra de Contexto ──────────────────────────────────────────
  
  getContextFields(tabsNode: BuilderNode): ContextFieldConfig[] {
    return (tabsNode.props as any)?.contextFields || [];
  }

  resolveContextValue(contextField: ContextFieldConfig): string {
    const dataPath = contextField.dataPath;
    if (!this.currentSelectedRowData || !dataPath) return '';
    const parts = dataPath.split('.');
    let value: any = this.currentSelectedRowData;
    for (const part of parts) {
      if (value == null) return '';
      value = value[part];
    }
    if (value == null) return '';

    // Caso 1: Valor já é um objeto {key, value} (campo search com showKey=true após interação)
    if (typeof value === 'object' && value !== null && 'key' in value && 'value' in value) {
      return `${value.key} - ${value.value}`;
    }

    // Caso 2: Campo de pesquisa com valor primitivo → buscar no cache ou resolver via API
    if (contextField.searchProperties) {
      const cacheKey = `${dataPath}:${value}`;
      const cached = this.searchDisplayCache.get(cacheKey);
      if (cached) return cached;
      // Se não está no cache, retorna apenas o código (a resolução assíncrona atualizará)
      return String(value);
    }

    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    return String(value);
  }

  /**
   * Resolve valores de campos de pesquisa (search/querySelect) via API.
   * Chamado quando currentSelectedRowData muda para buscar descrições dos códigos.
   */
  private resolveSearchFieldDisplayValues(): void {
    if (!this.currentSelectedRowData) return;

    // Encontrar o nó tabs para obter os contextFields configurados
    const nodes = this.builderService.currentState?.nodes || [];
    const tabsNode = nodes.find(n => n.type === 'tabs');
    if (!tabsNode) return;

    const contextFields = this.getContextFields(tabsNode);
    for (const cf of contextFields) {
      // Enriquecer campos antigos (sem searchProperties) buscando metadados do node source
      if (!cf.searchProperties) {
        const sourceNode = nodes.find(n =>
          (n.type === 'search' || n.type === 'querySelect') &&
          (n.props as any)?.dataPath === cf.dataPath
        );
        if (sourceNode) {
          cf.sourceType = sourceNode.type;
          if (sourceNode.type === 'search') {
            const sp = (sourceNode.props as any)?.searchProperties;
            if (sp?.entityName && sp?.fields?.keyField && sp?.fields?.valueField) {
              cf.searchProperties = {
                entityName: sp.entityName,
                fields: { keyField: sp.fields.keyField, valueField: sp.fields.valueField }
              };
            }
          } else if (sourceNode.type === 'querySelect') {
            const props = sourceNode.props as any;
            if (props.entityName) {
              cf.searchProperties = {
                entityName: props.entityName,
                fields: { keyField: props.keyField || 'id', valueField: props.valueField || 'name' }
              };
            }
          }
        }
      }
      if (!cf.searchProperties) continue;

      // Resolver o valor bruto
      const parts = cf.dataPath.split('.');
      let rawValue: any = this.currentSelectedRowData;
      for (const part of parts) {
        if (rawValue == null) break;
        rawValue = rawValue[part];
      }
      if (rawValue == null) continue;

      // Se já é um objeto {key, value}, colocar no cache diretamente
      if (typeof rawValue === 'object' && rawValue !== null && 'key' in rawValue && 'value' in rawValue) {
        this.searchDisplayCache.set(`${cf.dataPath}:${rawValue.key}`, `${rawValue.key} - ${rawValue.value}`);
        continue;
      }

      // Verificar se já está no cache
      const cacheKey = `${cf.dataPath}:${rawValue}`;
      if (this.searchDisplayCache.has(cacheKey)) continue;

      // Fazer chamada API para resolver o código → descrição
      const sp = cf.searchProperties;
      const body = {
        entityName: sp.entityName,
        condition: `${sp.fields.keyField} = '${rawValue}'`,
        showPresentation: false,
        showRelationshipData: false,
        showRelationship: false,
        showTableInformation: false,
      };

      this.http.post(body, 'JsonBrokerServices/find', false, true, true).pipe(take(1)).subscribe({
        next: (response: any) => {
          const list = response?.[sp.entityName];
          if (list?.length > 0) {
            const desc = list[0][sp.fields.valueField];
            if (desc) {
              this.searchDisplayCache.set(cacheKey, `${rawValue} - ${desc}`);
              this.cdr.markForCheck();
            }
          }
        },
        error: () => { /* Silenciosamente falha - mostra apenas o código */ }
      });
    }
  }

  getChildrenForTab(nodes: ReadonlyArray<BuilderNode>, parentId: string, tabId: string) {
    //  NOVO: Ordenar painéis pela propriedade 'order'
    return nodes
      .filter(n => n.parentId === parentId && n.tabId === tabId)
      .sort((a, b) => {
        const orderA = (a.props as any)?.order ?? 999;
        const orderB = (b.props as any)?.order ?? 999;
        return orderA - orderB;
      });
  }

  /**
   * Path efetivo para datagrid dentro de uma aba (ex: response.magalu.channels).
   * Quando a grade filha está na tab, o node pode ter só o path do pai (response.magalu);
   * aqui montamos parentPath + tabKey para isNestedGrid e loadNestedData funcionarem.
   * Lista principal: childPath com exatamente 2 partes -> sempre retornar childPath para usar loadData (evitar grade vazia).
   */
  getEffectiveDataPathForTabGrid(tabsNode: BuilderNode, tabItem: { id?: string; name?: string; key?: string; field?: string }, gridChild: BuilderNode): string | undefined {
    const childPath = (gridChild.props as any)?.dataPath as string | undefined;
    const segments = childPath ? childPath.split('.').filter(Boolean) : [];
    // Grade com path de 2 partes = lista principal; nunca concatenar key para não virar nested e deixar células vazias
    if (segments.length === 2) {
      return childPath;
    }
    // Se a grade já tem path aninhado (ex: response.magalu.channels), usar como está
    if (segments.length > 2) {
      return childPath;
    }
    const key = (tabItem as any)?.key ?? (tabItem as any)?.field ?? tabItem?.id;
    const parentPath = (tabsNode?.props as any)?.parentDataPath ?? this.dataSyncService.getMainSelection()?.arrayPath ?? childPath;
    if (parentPath && key) {
      return parentPath + '.' + key;
    }
    return childPath;
  }

  /**
   * NOVO: Verifica se um dataPath é aninhado (3+ segmentos)
   * Ex: "response.magalu" -> false (grade principal)
   * Ex: "response.magalu.channels" -> true (grade aninhada)
   */
  isNestedDataPath(dataPath: string | undefined): boolean {
    if (!dataPath) return false;
    const segments = dataPath.split('.').filter(Boolean);
    return segments.length > 2;
  }

  /**
   *  NOVO: Retorna os campos filhos de um Row (container horizontal)
   */
  getChildrenOfRow(nodes: ReadonlyArray<BuilderNode>, rowId: string) {
    // ✅ CORRIGIDO: Ordenar filhos do Row pela propriedade 'order' (igual ao panel)
    return nodes
      .filter(n => n.parentId === rowId)
      .sort((a, b) => {
        const orderA = (a.props as any)?.order ?? 999;
        const orderB = (b.props as any)?.order ?? 999;
        return orderA - orderB;
      });
  }

  getChildrenForPanel(nodes: ReadonlyArray<BuilderNode>, panelId: string) {
    //  CORRIGIDO: Ordenar filhos do panel pela propriedade 'order'
    return nodes
      .filter(n => n.parentId === panelId)
      .sort((a, b) => {
        const orderA = (a.props as any)?.order ?? 999;
        const orderB = (b.props as any)?.order ?? 999;
        return orderA - orderB;
      });
  }

  getRootNodes(nodes: ReadonlyArray<BuilderNode>) {
    return nodes.filter(n => !n.parentId);
  }

  onTabsIndexChange(node: BuilderNode, index: number): void {
    const items = this.getTabItems(node);
    const active = items[index]?.id;
    if (active) this.builderService.setActiveTab(node.id, active);
  }

  onSelectChild(node: BuilderNode, event: MouseEvent): void {
    // Desabilita seleção em modo view
    if (this.isViewMode) return;
    
    // Seleção corrigida para nós filhos para que o Inspetor se vincule ao nó correto
    event.stopPropagation();
    this.builderService.selectNode(node.id);
  }

  onRemoveNode(node: BuilderNode, event: MouseEvent): void {
    // Desabilita remoção em modo view
    if (this.isViewMode) return;
    
    event.stopPropagation();
    
    // Adicionar confirmação com modal do NG Zorro
    const componentLabel = (node.props as any)?.label || node.type;
    
    this.modal.confirm({
      nzTitle: 'Confirmar Exclusão',
      nzContent: `Deseja remover o componente "${componentLabel}"?<br><br>Esta ação não pode ser desfeita.`,
      nzOkText: 'Sim, Remover',
      nzOkDanger: true,
      nzCancelText: 'Cancelar',
      nzOnOk: () => {
        this.builderService.removeNode(node.id);
        this.cdr.markForCheck();
      }
    });
  }

  onRemoveTab(tabsNode: BuilderNode, tabId: string, event: MouseEvent): void {
    // Desabilita remoção em modo view
    if (this.isViewMode) return;
    
    event.stopPropagation();
    // TODO: Substituir por diálogo de confirmação adequado usando i18n
    const ok = window.confirm('Remover esta aba e seus campos?');
    if (!ok) return;
    this.builderService.removeTabItem(tabsNode.id, tabId);
  }

  getActiveTabId(tabsNode: BuilderNode): string | null {
    const items = this.getTabItems(tabsNode);
    return this.builderService.getActiveTabId(tabsNode.id, items as any);
  }

  togglePanelCollapse(panelNode: BuilderNode): void {
    const currentCollapsed = (panelNode.props as any)?.collapsed || false;
    this.builderService.updateNodeProps(panelNode.id, { collapsed: !currentCollapsed });
    this.cdr.markForCheck();
  }

  getPanelStyles(node: BuilderNode): { [key: string]: string } {
    const props = node.props as any;
    return {
      // CSS custom properties para estilização dinâmica do painel
      '--panel-border-color': props?.panelBorderColor || null,
      '--panel-header-bg-color': props?.panelHeaderBackgroundColor || null,
      '--panel-header-text-color': props?.panelHeaderTextColor || null,
      '--panel-content-bg-color': props?.panelContentBackgroundColor || null,
      // Fallback para propriedades antigas (mantém compatibilidade)
      'background-color': props?.panelContentBackgroundColor || props?.backgroundColor || null,
      'color': props?.panelHeaderTextColor || props?.textColor || null
    };
  }


  onDropField(event: any, panelNode: BuilderNode): void {
    // TODO: Implementar lógica de drag & drop para fields dentro do panel
  }

  trackByColumnKey(index: number, column: any): string {
    return column.key || `col_${index}`;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  trackByTabId(index: number, item: any): string {
    return item.id || `tab_${index}`;
  }


  getGridPosition(nodeId: string, positions: Map<string, GridPosition> | null): GridPosition | null {
    return positions?.get(nodeId) || null;
  }

  getChildGridPosition(childId: string, parentId: string, tabId: string, nodes: ReadonlyArray<BuilderNode>): GridPosition | null {
    // ✅ IMPORTANTE: Obter filhos JÁ ORDENADOS pela propriedade 'order'
    const tabChildren = nodes
      .filter(n => n.parentId === parentId && n.tabId === tabId)
      .sort((a, b) => {
        const orderA = (a.props as any)?.order ?? 999;
        const orderB = (b.props as any)?.order ?? 999;
        return orderA - orderB;
      });
    
    // Calcula posições da grade baseado na ordem dos filhos
    const childPositions = this.gridLayoutService.calculateChildGridPosition([...tabChildren]);
    
    return childPositions.get(childId) || null;
  }

  // NOVA FUNCIONALIDADE: Método específico para Panel (sem tabId)
  getPanelChildGridPosition(childId: string, parentId: string, nodes: ReadonlyArray<BuilderNode>): GridPosition | null {
    // ✅ IMPORTANTE: Obter filhos JÁ ORDENADOS pela propriedade 'order'
    const panelChildren = nodes
      .filter(n => n.parentId === parentId)
      .sort((a, b) => {
        const orderA = (a.props as any)?.order ?? 999;
        const orderB = (b.props as any)?.order ?? 999;
        return orderA - orderB;
      });
    
    // Calcula posições da grade apenas para estes filhos
    const childPositions = this.gridLayoutService.calculateChildGridPosition([...panelChildren]);
    
    return childPositions.get(childId) || null;
  }

  // Removido getTabContentGridHeight - não é mais necessário pois usamos grade com dimensionamento automático

  getGridStyle(position: GridPosition | null, nodeProps?: any, parentId?: string, nodeId?: string): { [key: string]: string } {
    if (!position) return {};

    if (nodeId && this.samePanelNonInlineGridParents.has(nodeId)) {
      return {
        gridRow: 'auto',
        gridColumn: `${position.col + 1} / ${position.col + position.colSpan + 1}`
      };
    }

    if (parentId && this.panelsWithFlipActiveGrid.has(parentId)) {
      return {
        gridRow: 'auto',
        gridColumn: `${position.col + 1} / ${position.col + position.colSpan + 1}`
      };
    }
    if (parentId && this.samePanelNonInlineGridParents.has(parentId)) {
      return {
        gridRow: 'auto',
        gridColumn: `${position.col + 1} / ${position.col + position.colSpan + 1}`
      };
    }
    if (nodeProps?.field && this.flipActiveGridIds.has(nodeProps.field)) {
      return {
        gridRow: `${position.row + 1} / ${position.row + 2}`,
        gridColumn: `${position.col + 1} / ${position.col + position.colSpan + 1}`
      };
    }
    return this.gridLayoutService.getGridCellCSS(position);
  }

  /**
   *  NOVO: Combina estilos da grade com estilos customizados do wrapper do nó
   */
  getNodeWrapperStyle(node: BuilderNode, position: GridPosition | null): { [key: string]: string } {
    const gridStyles = this.getGridStyle(position, node.props as any, undefined, node.id);
    const props = node.props as any;
    
    // Se o nó tem propriedades de wrapper customizadas, adiciona elas
    const wrapperStyles: { [key: string]: string } = {};
    
    if (props?.wrapperBackgroundColor) {
      wrapperStyles['background'] = props.wrapperBackgroundColor;
      wrapperStyles['background-image'] = 'none'; // Remove gradiente padrão
    }
    
    if (props?.wrapperBorderColor) {
      wrapperStyles['border-color'] = props.wrapperBorderColor;
    }
    
    if (props?.wrapperBorderRadius) {
      wrapperStyles['border-radius'] = props.wrapperBorderRadius;
    }
    
    if (props?.wrapperPadding) {
      wrapperStyles['padding'] = props.wrapperPadding;
    }
    
    return { ...gridStyles, ...wrapperStyles };
  }

  toggleGridOverlay(): void {
    this.showGridOverlay = !this.showGridOverlay;
    this.cdr.markForCheck();
  }

  getGridTemplate() {
    return this.gridLayoutService.getGridTemplate();
  }

  getGridCells(height: number | null): number[] {
    const totalCells = (height || 10) * 24; // 24 colunas
    return Array.from({ length: totalCells }, (_, i) => i);
  }



  // CORREÇÃO CRÍTICA: Métodos auxiliares de rolagem para melhor UX com muitos componentes
  scrollToTop(): void {
    const scrollContainer = document.querySelector('.canvas-scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Se não há container de rolagem, rola a janela/pai
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  scrollToBottom(): void {
    const scrollContainer = document.querySelector('.canvas-scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
    } else {
      // Se não há container de rolagem, rola para o final do canvas
      const canvas = document.querySelector('.canvas-grid-container');
      if (canvas) {
        canvas.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  }

  // Auxiliar de performance: Verifica se temos muitos componentes (para possíveis otimizações)
  hasManyComponents(): boolean {
    return this.builderService.nodes$.pipe(
      map(nodes => nodes.length > 20)
    ) as any; // Será usado no template com async pipe
  }

  // Verifica se o conteúdo do canvas é rolável
  isCanvasScrollable(): boolean {
    const scrollContainer = document.querySelector('.canvas-scroll-container');
    if (scrollContainer) {
      return scrollContainer.scrollHeight > scrollContainer.clientHeight;
    }
    return false;
  }



  private handleSaveAction(): void {
    const componentData = this.saveService.collectComponentValues();
    
    if (componentData.length === 0) {
      return;
    }
    
    const validation = this.saveService.validateRequiredFields(componentData);
    
    if (!validation.isValid) {
      return;
    }
    
    const savePayload = {
      timestamp: new Date().toISOString(),
      components: componentData,
      metadata: {
        formId: `form-${Date.now()}`,
        version: "1.0",
        totalComponents: componentData.length
      }
    };
    
    const builderScreenComponent = this.getBuilderScreenComponent();
    if (builderScreenComponent && typeof builderScreenComponent.onSaveClick === 'function') {
      builderScreenComponent.onSaveClick();
    }
  }

  private handleCancelAction(): void {
    const builderScreenComponent = this.getBuilderScreenComponent();
    if (builderScreenComponent && typeof builderScreenComponent.onCancelClick === 'function') {
      builderScreenComponent.onCancelClick();
    }
  }

  private handleCustomButtonAction(node: BuilderNode): void {
    const componentData = this.saveService.collectComponentValues();

    if (componentData.length === 0) {
      return;
    }

    const validation = this.saveService.validateRequiredFields(componentData);

    if (!validation.isValid) {
      return;
    }

    const savePayload = {
      timestamp: new Date().toISOString(),
      components: componentData,
      metadata: {
        formId: `form-${Date.now()}`,
        version: "1.0",
        totalComponents: componentData.length
      }
    };
  }
  
  /**
   *  NOVO: Manipula botão do tipo "Executar Fluxo"
   * Coleta JSON completo com todas as alterações e envia para processamento
   */ 
  private handleExecuteFlowAction(node: BuilderNode): void {
    const buttonProps = node.props as ButtonProps;
    const flowDataMode = buttonProps.flowDataMode || 'full'; // Padrão: 'full'
    
    // Verificar modo de envio configurado
    if (flowDataMode === 'selectedItem') {
      // Modo: ENVIAR ITEM SELECIONADO
      this.executeFlowWithSelectedItem(node);
    } else {
      // Modo: ENVIAR JSON COMPLETO (padrão)
      this.executeFlowWithFullData(node);
    }
  }
  
  /**
   * Envia a linha PAI completa (grade principal) para o fluxo, incluindo todos os dados
   * aninhados (ex.: aba Channels, etc.). Assim o backend recebe sempre o registro pai
   * com filhos (channels, etc.), quer o usuário tenha clicado na linha da grade pai ou na filha.
   */
  private executeFlowWithSelectedItem(node: BuilderNode): void {
    const mainSelection = this.dataSyncService.getMainSelection();
    const mainRowFromStore = this.dataSyncService.getMainRowFromStore();
    
    if (mainRowFromStore != null) {
      // Há grade principal selecionada: enviar a linha pai completa do store (com channels, etc.)
      const itemToSend = mainRowFromStore;
      const nodes = this.builderService.currentState.nodes;
      const validation = this.formValidationService.validateRequiredFields(nodes, itemToSend);
      if (!validation.valid) {
        this.formValidationService.showValidationErrors(validation.errors);
        return;
      }
      this.executeWorkflow(itemToSend, node);
      return;
    }
    
    // Fallback: tela sem grade principal (ou sem seleção na principal) – enviar item atual
    const selectedItem = this.dataSyncService.getCurrentItem();
    const currentIndex = this.dataSyncService.getCurrentIndex();
    if (!selectedItem || currentIndex === -1) {
      this.message.warning('Selecione um item da grade antes de executar o fluxo');
      return;
    }
    const itemToSend = selectedItem;
    const nodes = this.builderService.currentState.nodes;
    const validation = this.formValidationService.validateRequiredFields(nodes, itemToSend);
    if (!validation.valid) {
      this.formValidationService.showValidationErrors(validation.errors);
      return;
    }
    this.executeWorkflow(itemToSend, node);
  }
  
  /**
   * Envia o JSON completo (comportamento padrão)
   */
  private executeFlowWithFullData(node: BuilderNode): void {
    const directData = this.dataBindingService.getCurrentData();
    const completeData = this.dataSyncService.getCompleteData();
    const finalData = completeData || directData;
    
    if (!finalData) {
      this.message.error('Nenhum dado disponível');
      return;
    }
    
    const nodes = this.builderService.currentState.nodes;
    const mainRowFromStore = this.dataSyncService.getMainRowFromStore();
    const validation = this.formValidationService.validateRequiredFields(nodes, finalData, mainRowFromStore);
    
    if (!validation.valid) {
      this.formValidationService.showValidationErrors(validation.errors);
      return;
    }
    
    this.executeWorkflow(finalData, node);
  }
  
  /**
   *  NOVO: Executa o workflow com os dados coletados
   */
  private executeWorkflow(data: any, buttonNode: BuilderNode): void {
    const buttonProps = buttonNode.props as ButtonProps;
    
    if (buttonProps.flowConfig && buttonProps.flowConfig.idConf) {
      const action = {
        NAME: 'Executar Fluxo',
        ICON: 'rocket',
        IDCONF: buttonProps.flowConfig.idConf.toString(),
        CODECOMPANY: (buttonProps.flowConfig.codeCompany || this.uas.user.codecompany).toString(),
        TYPE: buttonProps.flowConfig.newMode ? 'NM' : 'C', // NM = motor novo (runOnlineAsync); C = antigo
        EXPRESSION: JSON.stringify({
          idConf: buttonProps.flowConfig.idConf,
          codeCompany: buttonProps.flowConfig.codeCompany || this.uas.user.codecompany,
          showCodeCompany: buttonProps.flowConfig.showCodeCompany
        }),
        SELECTED: null,
        IDINSTANCE: null,
        IDACTION: null,
      };
      
      this.dialogService.openGenericDialog(
        FlowModalComponent,
        {
          action: action,
          selected: [data],
          parameters: {},
          function: null
        },
        (result) => {
          // Fluxo executado com sucesso
        }
      );
    } else if (buttonProps.flowId) {
      this.message.info('Executando workflow...');
    } else {
      this.message.success('Dados coletados!');
    }
  }

  private getBuilderScreenComponent(): any {
    // Tenta acessar o componente pai builder screen
    // Esta é uma abordagem simples - numa aplicação mais complexa, você usaria um service ou event emitter
    let element = document.querySelector('app-builder-screen-beta');
    return (element as any)?.__ngContext__?.[8]; // Instância do componente Angular
  }

  // Spreadsheet event handlers
  onSpreadsheetDataChange(nodeId: string, data: any): void {
    this.builderService.updateNodeProps(nodeId, { data });
  }

  onSpreadsheetSelectionChange(nodeId: string, selection: any): void {
    if (selection === null || selection === undefined) {
      return;
    }
    
    // Handle selection change if needed
    // Aqui você pode implementar lógica específica para seleção de células
  }

  /**
   * Verifica se o tipo de componente é um componente webhook que deve usar o webhook-adapter-beta
   */
  isWebhookComponent(type: string): boolean {
    const webhookTypes = [
      'tag', 'keyValueObject', 'keyValue', 'search', 'querySelect', 
      'divider', 'select', 'radio', 'checkbox', 'date', 'time', 
      'dateTime', 'file', 'color', 'range', 'password'
    ];
    return webhookTypes.includes(type);
  }

  /**
   * Manipula mudanças no valor do switch e aplica controle de abas se configurado
   */
  onSwitchChange(node: BuilderNode, value: boolean): void {
    // Atualizar propriedades do node
    this.builderService.updateNodeProps(node.id, { defaultValue: value });
    
    // Aplicar controle de abas se configurado
    const tabControl = (node.props as any)?.tabControl;
    if (tabControl?.enabled) {
      this.tabControlService.applyTabControl(value, tabControl);
    }
  }

  /**
   * Obtém o caminho completo do ícone da aba
   */
  getTabIconPath(iconValue: string): string {
    if (!iconValue) return '';
    
    // Se já é um caminho completo, usar diretamente
    if (iconValue.startsWith('assets/')) {
      return iconValue;
    }
    
    // ✅ CORREÇÃO: Não tentar carregar ícones Ant Design ou customizados como arquivos
    // Ícones padrão do Ant Design (form, layout, etc.) são renderizados via <nz-icon>
    // Ícones customizados (custom:meli, etc.) são renderizados via nz-icon service
    const antDesignIcons = [
      'form', 'layout', 'appstore', 'setting', 'file-text', 'database',
      'container', 'edit', 'plus-square', 'shopping-cart', 'home',
      'user', 'team', 'project', 'folder', 'file', 'save', 'delete'
    ];
    
    // Se é ícone padrão do Ant Design ou customizado, retornar vazio (será renderizado via nz-icon)
    if (antDesignIcons.includes(iconValue) || iconValue.startsWith('custom:')) {
      return '';
    }
    
    // Construir caminho baseado no valor (apenas para ícones que são arquivos reais)
    // Verificar se tem extensão
    const hasExtension = iconValue.includes('.');
    if (hasExtension) {
      return `assets/image/${iconValue}`;
    } else {
      // Assumir SVG por padrão se não tem extensão
      return `assets/image/${iconValue}.svg`;
    }
  }

  /**
   * Obtém o título da aba (não usado mais, mantido para compatibilidade)
   */
  getTabTitleWithIcon(tabItem: any): string {
    return tabItem.name;
  }

  /**
   * Método público para testar aplicação de ícones manualmente
   */
  public testApplyIcons() {
    this.applyTabIcons();
  }

  /**
   * Aplica ícones nas abas após a renderização
   */
  ngOnInit(): void {
    //  NOVO: Escutar mudanças de Master-Detail
    this.masterDetailSubscription = this.masterDetailService.selectedRow$
      .subscribe((state: MasterDetailState) => {
        if (state.selectedRow) {
          // Grid principal = arrayPath com no máximo 2 partes (ex: response.item_list)
          const isMainGrid = !state.arrayPath || state.arrayPath.split('.').filter(Boolean).length <= 2;
          
          if (isMainGrid) {
            this.currentSelectedRowData = state.selectedRow;
            this.currentFormPanelId = state.targetPanelId || null;
            this.currentFormPanelIds = state.targetPanelIds || [];
            this.resolveSearchFieldDisplayValues();
          }
          
          const panelIds = state.targetPanelIds && state.targetPanelIds.length > 0
            ? state.targetPanelIds
            : (state.targetPanelId ? [state.targetPanelId] : []);
          const masterPanelIds = this.getMasterFormPanelIds();

          // CORREÇÃO: Se panelIds está vazio, usar masterPanelIds como fallback
          // Isso permite preencher formulários mesmo quando a grid não tem panelIds configurados
          const panelIdsToFill = panelIds.length > 0
            ? panelIds.filter(id => masterPanelIds.includes(id))
            : masterPanelIds;

          if (panelIdsToFill.length > 0 && isMainGrid) {
            const selectedRow = state.selectedRow;
            setTimeout(() => {
              panelIdsToFill.forEach(panelId => {
                this.fillFormWithRowData(panelId, selectedRow);
              });
              // Reaplicar defaults após preencher formulário com dados da linha
              // Necessário para novos registros com campos vazios que possuem defaultValue
              setTimeout(() => {
                this.applyDefaultValuesToFields();
              }, 200);
            }, 50);
          }
          
          this.activeMasterDetailPanel = { 
            formPanelId: state.targetPanelId || ''
          };
          this.cdr.markForCheck();
        }
      });

    //  NOVO: Escutar mudanças nos dados globais para atualizar formulários
    // CORREÇÃO: Usar debounceTime para evitar múltiplas chamadas consecutivas (piscadas)
    this.dataBindingSubscription = this.dataBindingService.getData$()
      .pipe(debounceTime(150)) // Aguardar 150ms antes de processar
      .subscribe((data: any) => {
        //  Prevenir loop infinito: só atualiza se não estamos já atualizando
        if (this.isUpdatingFromGlobalData) {
          return;
        }

        // Barra de Contexto: SEMPRE atualizar currentSelectedRowData para refletir
        // edições de formulário em tempo real, mesmo quando isUpdatingFromFormChange = true.
        // Isso garante que a barra de contexto mostre valores atualizados.
        if (data && this.currentSelectedRowData) {
          this.refreshContextStripData(data);
        }

        // CORREÇÃO: Bloquear quando edição vem do formulário
        // Isso evita que o formulário seja re-preenchido enquanto usuário edita
        // NOTA: isUpdatingFromFormChange é setado apenas pelo onFieldChange do canvas (eventos DOM),
        // mas NÃO é setado por webhook-adapters (que usam valueChanges → dataSyncService.onFormFieldChange).
        // Por isso, também verificamos dataSyncService.isUpdatingFromForm como fallback robusto.
        if (this.isUpdatingFromFormChange || (this.dataSyncService && this.dataSyncService.isUpdatingFromForm)) {
          return;
        }

        // CORREÇÃO: Guard de isCellEditing RE-ADICIONADA.
        // Agora é seguro porque o path shouldMaintainSelection no data-grid usa
        // skipTableRecreation=true, eliminando:
        //   1. Destruição assíncrona da tabela (que causava blur events cascata)
        //   2. Perda de seleção (displayData agora é atualizado sincronamente)
        // Sem blur events espúrios, a flag isCellEditing é limpa de forma confiável
        // pelo timer de onCellBlur (300ms). O re-emissão de getData$ acontece DEPOIS
        // de isCellEditing=false, e o debounce de 150ms do canvas garante que
        // updateFormFieldsFromGlobalData roda quando isCellEditing é definitivamente false.
        if (this.dataSyncService && this.dataSyncService.isCellEditing) {
          return;
        }

        const hasPanels = this.currentFormPanelIds.length > 0 || this.currentFormPanelId;
        
        if (data && hasPanels && this.currentSelectedRowData) {
          this.updateFormFieldsFromGlobalData();
        }
      });

    //  NOVO: Executar eventos onInit após inicialização
    // Usar setTimeout para garantir que todos os componentes foram montados
    setTimeout(() => {
      this.executeInitEvents();
      // Aplicar valores default aos campos que não têm dados vinculados
      this.applyDefaultValuesToFields();
      // Auto-adicionar primeira linha nas grades configuradas
      this.executeAutoAddRows();
    }, 500);
  }

  /**
   * Aplica valores default aos campos que não têm dados vinculados
   * Executa após a inicialização para garantir que os campos estão renderizados
   */
  private applyDefaultValuesToFields(): void {
    const nodes = this.builderService.currentState.nodes;
    const currentData = this.dataBindingService.getCurrentData();
    
    nodes.forEach(node => {
      const props = node.props as any;
      
      // Calcular defaultValue: verificar props.defaultValue ou options com isDefault (para dropdowns)
      let defaultValue = props.defaultValue;
      
      // Para dropdowns/selects, verificar se há opção marcada como isDefault
      if ((node.type === 'dropdown' || node.type === 'select') && 
          (defaultValue === undefined || defaultValue === null) && 
          props.options && Array.isArray(props.options)) {
        const defaultOption = props.options.find((opt: any) => opt.isDefault);
        if (defaultOption) {
          defaultValue = defaultOption.value;
        }
      }
      
      // Verificar se o campo tem um valor default configurado
      if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
        // Verificar se o campo não tem dados vinculados (ou se os dados estão vazios)
        const bindingPath = props.binding || props.dataPath;
        let hasData = false;
        
        if (bindingPath && currentData) {
          const boundValue = this.dataBindingService.resolvePath(currentData, bindingPath);
          hasData = boundValue !== null && boundValue !== undefined && boundValue !== '';
        }
        
        // Se não tem dados vinculados, aplicar o valor default
        if (!hasData) {
          this.setFieldValue(node.id, defaultValue);
        }
      }
    });
  }

  /**
   * Executa auto-adição de primeira linha nas grades configuradas com autoAddFirstRow.
   * Respeita a ordem (autoAddOrder) e executa sequencialmente para permitir
   * que grades filhas dependam da seleção da grade pai.
   */
  private executeAutoAddRows(): void {
    const nodes = this.builderService.currentState.nodes;
    const currentData = this.dataBindingService.getCurrentData();

    // Coletar todas as grades com autoAddFirstRow ativo
    const autoAddGrids = nodes
      .filter(n => n.type === 'datagrid' && (n.props as any).autoAddFirstRow === true)
      .map(n => ({
        node: n,
        dataPath: (n.props as any).dataPath as string,
        order: (n.props as any).autoAddOrder ?? 0,
        field: (n.props as any).field as string,
        masterDetailFormPanel: (n.props as any).masterDetailFormPanel as string | undefined,
        masterDetailFormPanels: (n.props as any).masterDetailFormPanels as string[] | undefined,
        columns: (n.props as any).columns || []
      }))
      .filter(g => !!g.dataPath)
      .sort((a, b) => a.order - b.order);

    if (autoAddGrids.length === 0) return;

    // Executar sequencialmente com delay entre cada grade
    let chainDelay = 0;
    const DELAY_BETWEEN_GRIDS = 400; // ms para permitir propagação de seleção

    autoAddGrids.forEach((grid, index) => {
      setTimeout(() => {
        // Verificar se o array está vazio (não adicionar se já tem dados)
        const data = this.dataBindingService.getCurrentData();
        const pathParts = grid.dataPath.split('.');
        const isNestedGrid = pathParts.length > 2;
        let array: any[] | null = null;

        if (isNestedGrid) {
          // Para grade aninhada, resolver com índice do pai selecionado
          const mainSel = this.dataSyncService.mainSelectionSubject.value;
          if (mainSel.index === -1) return; // Sem pai selecionado, não adicionar

          const parentArrayPath = mainSel.arrayPath;
          let nestedPath = grid.dataPath;
          if (grid.dataPath.startsWith(parentArrayPath + '.')) {
            nestedPath = grid.dataPath.substring(parentArrayPath.length + 1);
          }
          const fullPath = `${parentArrayPath}[${mainSel.index}].${nestedPath}`;
          const resolved = this.dataBindingService.resolvePath(data, fullPath);
          array = Array.isArray(resolved) ? resolved : null;
        } else {
          const resolved = this.dataBindingService.resolvePath(data, grid.dataPath);
          if (Array.isArray(resolved)) {
            array = resolved;
          } else if (resolved && typeof resolved === 'object') {
            // Tentar encontrar primeiro array dentro do objeto
            const keys = Object.keys(resolved);
            for (const key of keys) {
              if (Array.isArray(resolved[key])) {
                array = resolved[key];
                break;
              }
            }
          }
        }

        // Só adicionar se array está vazio ou não existe
        if (array === null || array.length === 0) {
          // Montar panelIds
          let panelIds: string[] | undefined;
          if (grid.masterDetailFormPanels && grid.masterDetailFormPanels.length > 0) {
            panelIds = grid.masterDetailFormPanels;
          } else if (grid.masterDetailFormPanel) {
            panelIds = [grid.masterDetailFormPanel];
          }

          // Coletar defaults dos campos do formulário
          const defaultValues = this.collectDefaultValuesForGrid(panelIds);

          this.dataSyncService.addGridRow(
            grid.dataPath,
            undefined,
            grid.columns,
            grid.field || 'auto-add-grid',
            grid.masterDetailFormPanel,
            panelIds,
            defaultValues,
            true
          );
        }
      }, chainDelay);

      chainDelay += DELAY_BETWEEN_GRIDS;
    });

    // Reaplicar defaults nos campos após todas as grades terem sido processadas
    setTimeout(() => {
      this.applyDefaultValuesToFields();
    }, chainDelay + 300);
  }

  /**
   * Coleta valores default dos BuilderNodes que pertencem aos painéis informados.
   * Usado pelo executeAutoAddRows para incluir defaults ao criar a primeira linha.
   */
  private collectDefaultValuesForGrid(panelIds?: string[]): Record<string, any> {
    const defaults: Record<string, any> = {};
    if (!panelIds || panelIds.length === 0) return defaults;

    const allNodes = this.builderService.currentState.nodes;

    allNodes.forEach(node => {
      if (!node.parentId) return;

      // Verificar se o node está dentro de algum dos painéis (considerando rows)
      let current: any = node;
      let insidePanel = false;
      while (current?.parentId) {
        if (panelIds.includes(current.parentId)) {
          insidePanel = true;
          break;
        }
        current = allNodes.find(n => n.id === current.parentId);
      }
      if (!insidePanel) return;

      const props = node.props as any;
      const dataPath = props.dataPath || props.binding;
      if (!dataPath) return;

      let defaultValue = props.defaultValue;

      // Para dropdowns/selects, verificar opção marcada como isDefault
      if ((node.type === 'dropdown' || node.type === 'select') &&
          (defaultValue === undefined || defaultValue === null) &&
          props.options && Array.isArray(props.options)) {
        const defaultOption = props.options.find((opt: any) => opt.isDefault);
        if (defaultOption) {
          defaultValue = defaultOption.value;
        }
      }

      if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
        defaults[dataPath] = defaultValue;
      }
    });

    return defaults;
  }

  ngAfterViewInit() {
    // Observa mudanças no DOM para aplicar ícones
    setTimeout(() => {
      this.applyTabIcons();
    }, 500);
    
    // Observa mudanças no estado para reaplicar ícones (com debounce)
    let debounceTimer: any;
    this.builderService.state$.subscribe(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.applyTabIcons();
      }, 200);
    });

    //  NOVO: Adicionar listeners para sincronização de campos
    this.setupFieldSynchronization();
  }

  /**
   *  NOVO: Configura listeners para sincronizar campos do canvas com DataSyncService
   */
  private setupFieldSynchronization(): void {
    // Aguardar um pouco para garantir que o DOM está pronto
    setTimeout(() => {
      const container = document.querySelector('.canvas-grid-container');
      if (!container) {
        return;
      }

      container.addEventListener('change', (event: Event) => {
        const target = event.target as HTMLElement;
        
        if (this.isFormInputElement(target)) {
          this.handleFieldChange(event);
        }
      });

      container.addEventListener('blur', (event: Event) => {
        const target = event.target as HTMLElement;
        
        if (this.isFormInputElement(target)) {
          this.handleFieldChange(event);
        }
      }, true);
    }, 1000);
  }
  
  /**
   *  Verifica se o elemento é um campo de formulário
   */
  private isFormInputElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      element.hasAttribute('contenteditable') ||
      element.classList.contains('ant-input') ||
      element.classList.contains('ant-input-number') ||
      element.classList.contains('ant-select') ||
      element.classList.contains('ant-picker')
    );
  }

  /**
   *  NOVO: Manipula mudanças em campos do formulário
   */
  private handleFieldChange(event: Event): void {
    const target = event.target as HTMLElement;
    
    const componentId = target.getAttribute('data-component-id');
    if (!componentId) {
      return;
    }
    
    if (!event.isTrusted && this.isUpdatingFromGlobalData) {
      return;
    }

    const node = this.builderService.currentState.nodes.find(n => n.id === componentId);
    if (!node) {
      return;
    }

    const fieldName = this.extractFieldName(node);
    if (!fieldName) {
      return;
    }

    // Obter o novo valor baseado no tipo de input
    let newValue: any;
    
    if (target instanceof HTMLInputElement) {
      if (target.type === 'checkbox') {
        newValue = target.checked;
      } else if (target.type === 'number') {
        newValue = target.valueAsNumber;
      } else {
        newValue = target.value;
      }
    } else if (target instanceof HTMLTextAreaElement) {
      newValue = target.value;
    } else if (target instanceof HTMLSelectElement) {
      newValue = target.value;
    } else {
      // Para componentes ng-zorro, tentar obter valor do ngModel
      newValue = (target as any).value || (target as any).ngModel;
    }

    // Setar flag para evitar atualizar formulários quando mudança vem do próprio formulário
    // CORREÇÃO: Aumentar timeout para 350ms para coincidir com janela do DataSyncService (300ms)
    this.isUpdatingFromFormChange = true;
    setTimeout(() => {
      this.isUpdatingFromFormChange = false;
    }, 350);

    const panelId = this.builderService.getContainingPanelId(node.id);
    // Preferir a grade cuja linha está sendo exibida no formulário (current = pai ou filha)
    const formDisplayArrayPath = this.dataSyncService.getCurrentSelection()?.arrayPath
      ?? this.dataSyncService.getMainSelection()?.arrayPath;
    const formContextArrayPath = panelId
      ? this.builderService.getGridDataPathForPanel(panelId, formDisplayArrayPath)
      : undefined;
    this.dataSyncService.onFormFieldChange(fieldName, newValue, formContextArrayPath);
    
    // CORREÇÃO: Sincronizar outros campos com mesmo dataPath
    // Isso garante que campos duplicados (ex: dentro de Row) sejam atualizados
    this.syncOtherFieldsWithSameDataPath(node.id, fieldName, newValue);
  }
  
  /**
   * Sincroniza outros campos que têm o mesmo dataPath
   * Usado quando um campo é editado para manter campos duplicados em sincronia
   */
  private syncOtherFieldsWithSameDataPath(sourceNodeId: string, dataPath: string, value: any): void {
    const allNodes = this.builderService.currentState.nodes;
    
    // Encontrar todos os campos com o mesmo dataPath (exceto o que originou a mudança)
    const otherFieldsWithSamePath = allNodes.filter(node => {
      if (node.id === sourceNodeId) return false; // Ignorar o campo que originou
      if (!this.isFormField(node.type)) return false;
      
      const nodeDataPath = (node.props as any)?.dataPath;
      return nodeDataPath === dataPath;
    });
    
    if (otherFieldsWithSamePath.length === 0) {
      return;
    }
    
    // Atualizar cada campo com o novo valor
    otherFieldsWithSamePath.forEach(field => {
      this.setFieldValue(field.id, value);
    });
  }

  private applyTabIcons() {
    try {
      // Encontra todos os tabsets no canvas
      const tabsets = document.querySelectorAll('.canvas-grid-container nz-tabs');
      
      if (tabsets.length === 0) {
        return;
      }
      
      // Busca dados das abas no estado atual usando o observable
      this.builderService.nodes$.pipe(
        take(1),
        map(nodes => nodes.filter(node => node.type === 'tabs'))
      ).subscribe(tabsNodes => {
        
        tabsets.forEach((tabset: any, tabsetIndex: number) => {
          // Encontra o node correspondente a este tabset
          const tabsNode = tabsNodes[tabsetIndex];
          if (!tabsNode || !tabsNode.props) {
            return;
          }
          
          // Cast para o tipo correto das tabs
          const tabsProps = tabsNode.props as any;
          if (!tabsProps.items || !Array.isArray(tabsProps.items)) {
            return;
          }
          
          // Encontra os botões de aba
          const tabButtons = tabset.querySelectorAll('.ant-tabs-tab');
          
          // Aplica ícones baseado nos dados do estado
          tabsProps.items.forEach((tabItem: any, index: number) => {
            const iconValue = tabItem.icon;
            
            if (!iconValue || iconValue === 'null' || iconValue === '') {
              return;
            }
            
            // Encontra o botão da aba correspondente
            const tabButton = tabButtons[index];
            
            if (!tabButton) {
              return;
            }
            
            // Remove ícone existente se houver
            const existingIcon = tabButton.querySelector('.tab-custom-icon, .tab-custom-mat-icon');
            if (existingIcon) {
              existingIcon.remove();
            }
            
            // Encontra o elemento de texto dentro do botão
            const tabBtn = tabButton.querySelector('.ant-tabs-tab-btn');
            
            if (!tabBtn) {
              return;
            }
            
            // ⭐ Verifica se é ícone customizado do MKTPICONS
            if (iconValue.startsWith('custom:')) {
              // Para ícones customizados, renderizar o SVG diretamente
              const iconName = iconValue.replace('custom:', '');
              
              // Tentar buscar o SVG do NzIconService
              const nzIconServiceInternal = this.nzIconService as any;
              let svgContent = '';
              
              // Método 1: Tentar _svgDefinitions (cache interno)
              if (nzIconServiceInternal._svgDefinitions) {
                const iconDef = nzIconServiceInternal._svgDefinitions.get(iconValue);
                if (typeof iconDef === 'string') {
                  svgContent = iconDef;
                } else if (iconDef && typeof iconDef === 'object') {
                  svgContent = iconDef.icon || iconDef.svg || iconDef.literal || '';
                }
              }
              
              // Método 2: Tentar _svgRenderedDefinitions
              if (!svgContent && nzIconServiceInternal._svgRenderedDefinitions) {
                const renderedDef = nzIconServiceInternal._svgRenderedDefinitions.get(iconValue);
                if (typeof renderedDef === 'string') {
                  svgContent = renderedDef;
                }
              }
              
              // Método 3: Tentar _svgElementsCache
              if (!svgContent && nzIconServiceInternal._svgElementsCache) {
                const cachedElement = nzIconServiceInternal._svgElementsCache.get(iconValue);
                if (cachedElement && cachedElement.outerHTML) {
                  svgContent = cachedElement.outerHTML;
                }
              }
              
              // Se encontrou o SVG, renderizar
              if (svgContent && typeof svgContent === 'string' && svgContent.trim().length > 0) {
                // Criar um span wrapper para o SVG
                const iconWrapper = document.createElement('span');
                iconWrapper.className = 'tab-custom-mat-icon';
                iconWrapper.style.width = '14px';
                iconWrapper.style.height = '14px';
                iconWrapper.style.marginRight = '6px';
                iconWrapper.style.verticalAlign = 'middle';
                iconWrapper.style.display = 'inline-flex';
                iconWrapper.style.alignItems = 'center';
                iconWrapper.style.justifyContent = 'center';
                
                // Inserir o SVG diretamente
                iconWrapper.innerHTML = svgContent;
                
                // Ajustar o tamanho do SVG interno
                const svgElement = iconWrapper.querySelector('svg');
                if (svgElement) {
                  svgElement.setAttribute('width', '14');
                  svgElement.setAttribute('height', '14');
                  svgElement.style.width = '14px';
                  svgElement.style.height = '14px';
                }
                
                // Insere o wrapper no início do botão
                tabBtn.insertBefore(iconWrapper, tabBtn.firstChild);
              } else {
                const placeholder = document.createElement('span');
                placeholder.className = 'tab-icon-placeholder';
                placeholder.style.width = '14px';
                placeholder.style.height = '14px';
                placeholder.style.marginRight = '6px';
                placeholder.style.display = 'inline-block';
                placeholder.style.backgroundColor = '#ccc';
                placeholder.style.borderRadius = '2px';
                placeholder.title = `Ícone: ${iconName}`;
                tabBtn.insertBefore(placeholder, tabBtn.firstChild);
              }
            } else {
              // Ícones estáticos usam <img>
              const iconPath = this.getTabIconPath(iconValue);
              
              if (iconPath) {
                const iconImg = document.createElement('img');
                iconImg.src = iconPath;
                iconImg.className = 'tab-custom-icon';
                iconImg.style.width = '14px';
                iconImg.style.height = '14px';
                iconImg.style.marginRight = '6px';
                iconImg.style.verticalAlign = 'middle';
                iconImg.style.display = 'inline-block';
                
                // Insere o ícone no início do botão
                tabBtn.insertBefore(iconImg, tabBtn.firstChild);
              }
            }
          });
        });
      });
    } catch {
      // Ícones: falha silenciosa
    }
  }

  /**
   * Handler de clique para botões
   * Executa ações configuradas apenas em modo 'view'
   */
  onButtonClick(node: BuilderNode, event: MouseEvent): void {
    event.stopPropagation();
    
    // Apenas executar ações em modo 'view'
    if (this.viewMode !== 'view') {
      return;
    }
    
    const props = node.props as ButtonProps;
    
    //  NOVO: Suporte para diferentes tipos de ação
    const action = props.action || props.actionType;
    
    // Detectar tipo de ação
    if (action === 'executeFlow' || action === 'execute-flow' || action === 'Executar Fluxo' || action === 'flow') {
      //  Todos esses tipos executam o fluxo com JSON completo
      this.handleExecuteFlowAction(node);
    } else if (action === 'save') {
      this.submitForm();
    } else if (action === 'cancel') {
      this.handleCancelAction();
    } else if (action === 'custom') {
      this.handleCustomButtonAction(node);
    }
  }

  /**
   * Executa fluxo MKTPCFG com os dados preenchidos na tela
   */
  private executeFlow(flowConfig: ButtonProps['flowConfig']): void {
    if (!flowConfig || !flowConfig.idConf) {
      this.message.warning('Configuração de fluxo não definida');
      return;
    }
    
    const formData = this.builderService.getFormData();
    
    // Montar action conforme padrão do sistema
    const action = {
      NAME: 'Executar Fluxo',
      ICON: 'rocket',
      IDCONF: flowConfig.idConf.toString(),
      CODECOMPANY: (flowConfig.codeCompany || this.uas.user.codecompany).toString(),
      TYPE: flowConfig.newMode ? 'NM' : 'C', // NM = motor novo (runOnlineAsync); C = antigo
      EXPRESSION: JSON.stringify({
        idConf: flowConfig.idConf,
        codeCompany: flowConfig.codeCompany || this.uas.user.codecompany,
        showCodeCompany: flowConfig.showCodeCompany
      }),
      SELECTED: null,
      IDINSTANCE: null,
      IDACTION: null,
      
    };
    
    // Abrir modal de fluxo
    this.dialogService.openGenericDialog(
      FlowModalComponent,
      {
        action: action,
        selected: [formData], // Array com objeto dos campos
        parameters: {},
        function: null
      },
      () => {
        // Callback após fechar modal
      }
    );
  }

  /**
   * Submete formulário (placeholder para implementação futura)
   */
  private submitForm(): void {
    this.builderService.getFormData();
    this.message.info('Funcionalidade de submit em desenvolvimento');
  }

  /**
   * Retorna IDs de painéis que são formulário de grid PRINCIPAL (mestre).
   * Usado para não preencher com linha pai os painéis de grids filhas (detail).
   * Critério: datagrid com dataPath de nível principal (≤2 partes, ex: response.item_list).
   */
  private getMasterFormPanelIds(): string[] {
    const nodes = this.builderService.currentState.nodes;
    const dataPathParts = (path: string) => (path || '').trim().split('.').filter(Boolean).length;
    const mainGridNodes = nodes.filter(
      n => n.type === 'datagrid' && dataPathParts((n.props as any)?.dataPath || '') <= 2
    );
    const panelIds: string[] = [];
    mainGridNodes.forEach(n => {
      const props = n.props as any;
      if (props.masterDetailFormPanels?.length) {
        panelIds.push(...props.masterDetailFormPanels);
      } else if (props.masterDetailFormPanel) {
        panelIds.push(props.masterDetailFormPanel);
      }
    });
    return [...new Set(panelIds)];
  }

  /**
   *  Preenche campos do formulário com dados da linha selecionada (Master-Detail).
   * Inclui campos que estão dentro de componentes row (usa getNodeIdsUnderPanel).
   */
  private fillFormWithRowData(panelId: string, rowData: any): void {
    const allNodes = this.builderService.currentState.nodes;
    const idsUnderPanel = this.builderService.getNodeIdsUnderPanel(allNodes, panelId);

    const formFields = allNodes.filter(node =>
      this.isFormField(node.type) && idsUnderPanel.has(node.id)
    );

    if (formFields.length === 0) {
      return;
    }

    formFields.forEach(field => {
      const fieldName = this.extractFieldName(field);
      let value = this.getValueByPath(rowData, fieldName);

      if (value !== undefined && value !== null) {
        const fieldType = field.type;
        const isSimpleInputField = ['text', 'textarea', 'number'].includes(fieldType);
        const isComplexValue = typeof value === 'object' && value !== null;

        if (isSimpleInputField && isComplexValue) {
          return;
        }

        this.setFieldValue(field.id, value);
      } else {
        // CORREÇÃO: Limpar campos que são null/undefined na nova linha selecionada.
        // Sem isso, campos de pesquisa (Empresa, Contrato, Produto) retinham valores
        // da linha anterior quando uma nova linha vazia era adicionada.
        this.setFieldValue(field.id, null);
      }
    });

    this.cdr.markForCheck();
  }

  /**
   *  NOVO: Atualiza campos do formulário a partir dos dados globais
   * Chamado quando DataBindingService emite novos dados
   */
  private updateFormFieldsFromGlobalData(): void {
    // CORREÇÃO: Debounce para evitar múltiplas atualizações consecutivas
    const now = Date.now();
    if ((now - this._lastFormUpdateTimestamp) < 100) {
      return;
    }
    this._lastFormUpdateTimestamp = now;

    if (this.dataSyncService.isUpdatingFromForm) {
      return;
    }
    // Não re-preencher formulário quando o contexto é grade FILHA (ex: canais).
    // O preenchimento foi feito ao selecionar a linha (fillNestedFormDirectly); re-preencher
    // aqui sobrescreve com dados errados e causa loop (valor volta ao anterior).
    const selection = this.dataSyncService.getCurrentSelection();
    const mainSelection = this.dataSyncService.getMainSelection();
    const isNestedForm = selection?.arrayPath && mainSelection?.arrayPath &&
      selection.arrayPath !== mainSelection.arrayPath &&
      selection.arrayPath.startsWith(mainSelection.arrayPath + '.');
    if (isNestedForm) {
      return;
    }
    const panelIds = this.currentFormPanelIds.length > 0 
      ? this.currentFormPanelIds 
      : (this.currentFormPanelId ? [this.currentFormPanelId] : []);
    const masterPanelIds = this.getMasterFormPanelIds();
    const panelIdsToUpdate = panelIds.filter(id => masterPanelIds.includes(id));
    
    if (panelIdsToUpdate.length === 0 || !this.currentSelectedRowData) {
      return;
    }

    const globalData = this.dataBindingService.getCurrentData();
    if (!globalData) {
      return;
    }

    const updatedRowData = this.findUpdatedRowInGlobalData(globalData, this.currentSelectedRowData);

    if (!updatedRowData) {
      return;
    }

    this.currentSelectedRowData = updatedRowData;
    this.resolveSearchFieldDisplayValues();

    const allNodes = this.builderService.currentState.nodes;
    const idsUnderPanels = new Set<string>();
    panelIdsToUpdate.forEach(pid => this.builderService.getNodeIdsUnderPanel(allNodes, pid).forEach(id => idsUnderPanels.add(id)));
    
    const formFields = allNodes.filter(node =>
      this.isFormField(node.type) && idsUnderPanels.has(node.id)
    );

    // CORREÇÃO: Usar fieldSync$ para atualizar campos em vez de setFieldValue (DOM).
    // setFieldValue dispara events DOM (input, change, blur) que cascateiam:
    //   DOM input event → webhook-adapter valueChanges → onFormFieldChange → getData$ emite → loop
    // fieldSync$ usa FormControl.setValue com emitEvent:false, evitando cascata.
    this.isUpdatingFromGlobalData = true;
    
    try {
      // Atualizar cada campo via fieldSync$ (sem cascata de eventos DOM)
      formFields.forEach(field => {
        const fieldName = this.extractFieldName(field);
        const newValue = this.getValueByPath(updatedRowData, fieldName);
        
        // CORREÇÃO: Pular valores null/undefined/vazio para NÃO limpar campos que já têm dados.
        // - O campo editado na grade já é sincronizado diretamente por processCellEdit (emitFieldSync)
        // - A limpeza explícita é feita por clearNestedForm (com targetNodeIds)
        // - Valores null no store (ex: propriedades não preenchidas) limpavam campos que tinham
        //   dados do preenchimento inicial (via selectedRow$ ou fillFormWithRowData)
        // NOTA: false e 0 são valores válidos e devem ser emitidos
        if (newValue === undefined || newValue === null) {
          return;
        }
        
        //  CRÍTICO: Ignorar objetos e arrays complexos em campos de texto/número
        const fieldType = field.type;
        const isSimpleInputField = ['text', 'textarea', 'number'].includes(fieldType);
        const isComplexValue = typeof newValue === 'object' && newValue !== null;
        
        if (isSimpleInputField && isComplexValue) {
          return;
        }
        
        // Usar fieldSync$ para atualizar: sourceNodeId='canvas_global_update' para que
        // TODOS os campos com esse dataPath recebam (nenhum será filtrado como "próprio")
        this.dataSyncService.emitFieldSync(fieldName, newValue, 'canvas_global_update');
      });

      // Forçar detecção de mudanças
      this.cdr.markForCheck();
    } finally {
      setTimeout(() => {
        this.isUpdatingFromGlobalData = false;
      }, 100);
    }
  }

  /**
   * Atualiza apenas o currentSelectedRowData a partir dos dados globais,
   * sem re-preencher formulários. Garante que a barra de contexto reflita
   * edições em tempo real (ex: usuario muda SKU no form, barra atualiza).
   */
  private refreshContextStripData(globalData: any): void {
    const updatedRow = this.findUpdatedRowInGlobalData(globalData, this.currentSelectedRowData);
    if (updatedRow) {
      this.currentSelectedRowData = updatedRow;
      this.resolveSearchFieldDisplayValues();
      this.cdr.markForCheck();
    }
  }

  /**
   *  NOVO: Encontra a linha atualizada nos dados globais
   * Usa mainSelection para grade principal; currentSelection (nested) para grade filha (ex: canais).
   * Assim o re-preenchimento do formulário usa a linha correta e evita loop (valor voltando ao anterior).
   */
  private findUpdatedRowInGlobalData(globalData: any, currentRow: any): any {
    const mainSelection = this.dataSyncService.getMainSelection();
    const selection = this.dataSyncService.getCurrentSelection();
    const arrayPath = selection?.arrayPath ?? mainSelection?.arrayPath;

    if (!arrayPath) {
      return null;
    }

    // CORREÇÃO: Usar getMainRowFromStore para obter sempre a linha atualizada do JSON
    // Isso é mais confiável do que tentar encontrar por índice/conteúdo
    const mainRowFromStore = this.dataSyncService.getMainRowFromStore();
    if (mainRowFromStore) {
      return mainRowFromStore;
    }

    // Grade filha (ex: response.magalu.channels): resolver linha pelo path completo com índice do pai
    const pathParts = arrayPath.split('.').filter(Boolean);
    if (pathParts.length > 2 && mainSelection?.arrayPath && selection.arrayPath?.startsWith(mainSelection.arrayPath + '.')) {
      const nestedPath = selection.arrayPath.replace(mainSelection.arrayPath + '.', '');
      const fullArrayPath = `${mainSelection.arrayPath}[${mainSelection.index}].${nestedPath}`;
      const dataArray = this.dataBindingService.resolvePath(globalData, fullArrayPath);
      if (Array.isArray(dataArray)) {
        const idx = selection.index ?? -1;
        if (idx >= 0 && idx < dataArray.length) {
          return dataArray[idx];
        }
      }
      return null;
    }

    // Grade principal (ex: response.magalu)
    const dataArray = this.dataBindingService.resolvePath(globalData, arrayPath);
    if (!Array.isArray(dataArray)) {
      return null;
    }

    const currentIndex = (pathParts.length <= 2 ? mainSelection : selection)?.index ?? -1;
    if (currentIndex >= 0 && currentIndex < dataArray.length) {
      return dataArray[currentIndex];
    }

    const identifierKeys = ['id', 'item_id', 'item_sku', 'sku', '_id', 'uuid'];
    for (const key of identifierKeys) {
      if (currentRow[key] !== undefined) {
        const found = dataArray.find(item => item[key] === currentRow[key]);
        if (found) {
          return found;
        }
      }
    }

    const currentIndexByContent = dataArray.findIndex(item =>
      JSON.stringify(item) === JSON.stringify(currentRow)
    );
    if (currentIndexByContent !== -1) {
      return dataArray[currentIndexByContent];
    }

    return null;
  }

  /**
   *  Verifica se o tipo é um campo de formulário
   */
  private isFormField(type: string): boolean {
    const formTypes = [
      'text', 'textarea', 'number', 'date', 
      'select', 'checkbox', 'switch', 'radio'
    ];
    return formTypes.includes(type);
  }

  /**
   *  Extrai o nome do campo para mapear com os dados
   */
  private extractFieldName(node: BuilderNode): string {
    // Prioridade: dataPath > field > label convertida
    const props = node.props as any;
    return props?.dataPath || 
           props?.field || 
           props?.label?.toLowerCase().replace(/\s+/g, '_') ||
           node.id;
  }

  /**
   *  NOVO: Obtém valor de um objeto usando path (suporta caminhos aninhados)
   * Ex: getValueByPath(obj, "address.street") retorna obj.address.street
   */
  private getValueByPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    
    // Se path não tem pontos, acesso direto
    if (!path.includes('.')) {
      return obj[path];
    }
    
    // Path aninhado: dividir e navegar
    const keys = path.split('.');
    let value = obj;
    
    for (const key of keys) {
      if (value === null || value === undefined) {
        return undefined;
      }
      value = value[key];
    }
    
    return value;
  }

  /**
   *  Define o valor de um campo (implementação robusta para Ant Design + Angular Forms)
   */
  private setFieldValue(fieldId: string, value: any, retryCount: number = 0): void {
    // NOVO: Se valor for null/undefined/vazio, verificar se existe defaultValue nas props do node
    if (value === null || value === undefined || value === '') {
      const node = this.builderService.currentState.nodes.find(n => n.id === fieldId);
      const defaultValue = (node?.props as any)?.defaultValue;
      if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
        value = defaultValue;
      }
    }

    // 1. Tentar encontrar o container do componente
    const container = document.querySelector(`[data-component-id="${fieldId}"]`);
    
    if (!container) {
      // ⚠️ Container não renderizado ainda - tentar novamente após delay (máximo 3 tentativas)
      if (retryCount < 3) {
        setTimeout(() => {
          this.setFieldValue(fieldId, value, retryCount + 1);
        }, 300 * (retryCount + 1)); // Delay: 300ms, 600ms, 900ms
        return;
      }
      // Após 3 tentativas, campo provavelmente está em aba não visível (não logar)
      return;
    }

    // 2. Tentar encontrar o input dentro do container (para nz-input-number, nz-input, etc)
    let inputElement = container.querySelector('input') as HTMLInputElement;
    
    // 3. Se não encontrar input, verificar se o próprio container é um input
    if (!inputElement && container instanceof HTMLInputElement) {
      inputElement = container;
    }

    // 4. Verificar se é um textarea
    let textareaElement = container.querySelector('textarea') as HTMLTextAreaElement;
    if (!textareaElement && container instanceof HTMLTextAreaElement) {
      textareaElement = container;
    }

    // 5. Verificar se é um nz-switch
    const switchElement = container.querySelector('nz-switch');
    
    // 5.1. Verificar se é um nz-select (dropdown)
    const selectElement = container.querySelector('nz-select');
    
    // 6. Definir valor baseado no tipo de elemento encontrado
    if (selectElement && inputElement && inputElement.classList.contains('ant-select-selection-search-input')) {
      // É um nz-select (dropdown)
      // Encontrar o componente webhook-adapter e chamar writeValue
      const webhookAdapter = (container as any).__ngContext__?.[8]; // Angular component instance
      
      if (webhookAdapter && typeof webhookAdapter.writeValue === 'function') {
        webhookAdapter.writeValue(value);
      }
    } else if (inputElement) {
      const inputType = inputElement.type;
      
      if (inputType === 'checkbox' || inputType === 'radio') {
        inputElement.checked = !!value;
        if (!this.isUpdatingFromGlobalData) {
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          inputElement.dispatchEvent(new Event('change', { bubbles: true }));
          inputElement.dispatchEvent(new Event('blur', { bubbles: true }));
        }
        
        //  Remover classes de erro
        this.removeFormErrorClasses(inputElement);
        setTimeout(() => this.removeFormErrorClasses(inputElement), 50);
        setTimeout(() => this.removeFormErrorClasses(inputElement), 200);
        
      } else if (inputType === 'number') {
        // Verificar se é um nz-input-number (componente NG-ZORRO)
        const numberComponent = container.querySelector('nz-input-number');
        
        if (numberComponent) {
          // É um nz-input-number - usar writeValue do webhook-adapter
          const webhookAdapter = (container as any).__ngContext__?.[8];
          
          if (webhookAdapter && typeof webhookAdapter.writeValue === 'function') {
            webhookAdapter.writeValue(value);
          }
        } else {
          if (value === undefined || value === null || value === '') {
            inputElement.value = '';
            (inputElement as any).ngModel = null;
          } else {
            inputElement.value = value.toString();
            (inputElement as any).ngModel = value;
          }
          
          // Disparar eventos
          // (o loop é prevenido pela flag dataSyncService.isUpdatingFromForm)
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          inputElement.dispatchEvent(new Event('change', { bubbles: true }));
          inputElement.dispatchEvent(new Event('blur', { bubbles: true }));
          
          // Remover classes de erro
          this.removeFormErrorClasses(inputElement);
          setTimeout(() => this.removeFormErrorClasses(inputElement), 50);
          setTimeout(() => this.removeFormErrorClasses(inputElement), 200);
        }
      } else {
        inputElement.value = value?.toString() || '';
        // (o loop é prevenido pela flag dataSyncService.isUpdatingFromForm)
        inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        inputElement.dispatchEvent(new Event('blur', { bubbles: true }));
        
        //  Remover classes de erro
        this.removeFormErrorClasses(inputElement);
        setTimeout(() => this.removeFormErrorClasses(inputElement), 50);
        setTimeout(() => this.removeFormErrorClasses(inputElement), 200);
      }
      
    } else if (textareaElement) {
      // Verificar se é um textarea NG-ZORRO (tem atributo nz-input)
      const isNzInput = textareaElement.hasAttribute('nz-input') || textareaElement.classList.contains('ant-input');
      
      if (isNzInput) {
        // É um textarea NG-ZORRO - usar writeValue do webhook-adapter
        const webhookAdapter = (container as any).__ngContext__?.[8];
        
        if (webhookAdapter && typeof webhookAdapter.writeValue === 'function') {
          webhookAdapter.writeValue(value);
        }
      } else {
        textareaElement.value = value?.toString() || '';
        if (!this.isUpdatingFromGlobalData) {
          textareaElement.dispatchEvent(new Event('input', { bubbles: true }));
          textareaElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.removeFormErrorClasses(textareaElement);
        setTimeout(() => this.removeFormErrorClasses(textareaElement), 50);
        setTimeout(() => this.removeFormErrorClasses(textareaElement), 200);
      }
      
    } else if (switchElement) {
      // NZ-Switch - usar writeValue do webhook-adapter
      // Tentar encontrar o webhook-adapter em múltiplos índices possíveis
      let webhookAdapter = null;
      const context = (container as any).__ngContext__;
      
      if (context && Array.isArray(context)) {
        // Tentar índices comuns onde o webhook-adapter pode estar
        for (const index of [8, 9, 10, 11, 12, 13, 14, 15]) {
          const candidate = context[index];
          if (candidate && typeof candidate.writeValue === 'function') {
            webhookAdapter = candidate;
            break;
          }
        }
      }
      
      if (webhookAdapter) {
        webhookAdapter.writeValue(value);
      }
    }
  }

  /**
   *  Remove classes de erro do Angular Forms (versão DEFINITIVA com force)
   */
  private removeFormErrorClasses(element: HTMLElement): void {
    // 1. Remover TODAS as classes de validação do Angular do elemento
    element.classList.remove('ng-invalid', 'ng-touched', 'ng-dirty', 'ng-pending');
    element.classList.add('ng-valid', 'ng-untouched', 'ng-pristine');
    
    // 2. Remover atributos ARIA de erro
    element.removeAttribute('aria-invalid');
    element.removeAttribute('aria-describedby');
    
    // 3. Remover classes do container pai direto (nz-form-item-control)
    const formControl = element.closest('.ant-form-item-control');
    if (formControl) {
      formControl.classList.remove('ant-form-item-has-error', 'ant-form-item-has-warning');
      formControl.classList.add('ant-form-item-has-success');
      
      // Remover TODAS as mensagens de erro
      const errorMsgs = formControl.querySelectorAll('.ant-form-item-explain, .ant-form-item-explain-error');
      errorMsgs.forEach(msg => msg.remove());
    }
    
    // 4. Remover classes do container de formulário completo
    const formItem = element.closest('.ant-form-item');
    if (formItem) {
      formItem.classList.remove('ant-form-item-has-error', 'ant-form-item-has-warning');
      formItem.classList.add('ant-form-item-has-success');
    }
    
    // 5. Remover classes de erro do wrapper do nz-input-number
    const numberWrapper = element.closest('.ant-input-number');
    if (numberWrapper) {
      numberWrapper.classList.remove('ant-input-number-status-error');
      numberWrapper.classList.add('ant-input-number-status-success');
    }
    
    // 6.  NOVO: FORÇAR remoção de estilos inline usando setProperty com prioridade
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      element.style.removeProperty('color');
      element.style.removeProperty('border-color');
      element.style.removeProperty('background-color');
      
      // Forçar cor preta no texto usando !important
      element.style.setProperty('color', 'rgba(0, 0, 0, 0.85)', 'important');
      element.style.setProperty('border-color', '#d9d9d9', 'important');
    }
    
    // 7.  NOVO: Instalar MutationObserver para monitorar mudanças de estilo
    this.installStyleWatcher(element);
  }

  /**
   *  NOVO: Instala um observador que previne estilos de erro
   */
  private installStyleWatcher(element: HTMLElement): void {
    // Verificar se já existe um observador
    const existingObserver = (element as any)._styleObserver;
    if (existingObserver) {
      return; // Já está monitorando
    }

    // Criar MutationObserver para monitorar mudanças de atributo 'style'
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target as HTMLElement;
          const computedColor = window.getComputedStyle(target).color;
          
          // Verificar se a cor está vermelha (rgb(255, 77, 79) é #ff4d4f do Ant Design)
          if (computedColor.includes('255, 77, 79') || 
              computedColor.includes('rgb(255, 77, 79)') ||
              target.style.color === 'rgb(255, 77, 79)' ||
              target.style.color === '#ff4d4f') {
            
            // Forçar cor preta novamente
            target.style.removeProperty('color');
            target.style.setProperty('color', 'rgba(0, 0, 0, 0.85)', 'important');
            target.style.removeProperty('border-color');
            target.style.setProperty('border-color', '#d9d9d9', 'important');
          }
        }
      });
    });

    // Observar mudanças no atributo 'style'
    observer.observe(element, {
      attributes: true,
      attributeFilter: ['style']
    });

    // Armazenar referência para cleanup futuro
    (element as any)._styleObserver = observer;
  }

  /**
   *  NOVO: Retorna o modo do Master-Detail Panel baseado no tamanho da tela
   */
  getMasterDetailMode(): 'slide' | 'sheet' {
    const width = window.innerWidth;
    return width >= 1024 ? 'slide' : 'sheet';
  }

  /**
   *  NOVO: Evento quando o Master-Detail Panel é fechado
   */
  onMasterDetailPanelClosed(): void {
    this.activeMasterDetailPanel = null;
    this.cdr.markForCheck();
  }

  // ── Drawer state ──────────────────────────────────────────────────
  public isDrawerVisible = false;
  public drawerPanelIds: string[] = [];
  public drawerWidth = 40;

  isPanelHiddenByViewMode(panelId: string): boolean {
    if (!panelId) return false;
    if (!this._nonInlinePanelIds.has(panelId)) return false;
    if (this.flipActivePanels.has(panelId)) return false;
    return true;
  }

  shouldHideNonGridChild(panelId: string, childType: string): boolean {
    if (this.isEditMode) return false;
    if (childType === 'datagrid') return false;
    if (!this.samePanelNonInlineGridParents.has(panelId)) return false;
    if (this.panelsWithFlipActiveGrid.has(panelId)) return false;
    return true;
  }

  refreshNonInlinePanelIds(): void {
    this._nonInlinePanelIds.clear();
    this.samePanelNonInlineGridParents.clear();
    const nodes = this.builderService.currentState.nodes;
    const panelNodeIds = new Set(nodes.filter(n => n.type === 'panel').map(n => n.id));

    const formFieldTypes = new Set([
      'text', 'textarea', 'number', 'switch', 'checkbox', 'select',
      'date', 'time', 'datetime', 'dropdown', 'search', 'tag', 'image', 'button', 'row'
    ]);

    for (const node of nodes) {
      if (node.type !== 'datagrid') continue;
      const props = node.props as any;
      const vm = props?.viewMode;
      if (!vm || vm === 'inline') continue;

      const gridParentId = node.parentId;

      const candidates: string[] = [];
      if (props.masterDetailFormPanel) candidates.push(props.masterDetailFormPanel);
      if (props.masterDetailFormPanels && Array.isArray(props.masterDetailFormPanels)) {
        candidates.push(...props.masterDetailFormPanels);
      }

      const hasValidExternalPanel = candidates.some(pid =>
        panelNodeIds.has(pid) && pid !== gridParentId
      );

      for (const pid of candidates) {
        if (!panelNodeIds.has(pid)) continue;
        if (pid === gridParentId) continue;
        this._nonInlinePanelIds.add(pid);
      }

      if (!hasValidExternalPanel && gridParentId) {
        const hasSiblingFields = nodes.some(n =>
          n.parentId === gridParentId && n.id !== node.id && formFieldTypes.has(n.type)
        );
        if (hasSiblingFields) {
          this.samePanelNonInlineGridParents.add(gridParentId);
        }
      }
    }
  }

  onFlipStateChanged(event: { active: boolean; gridId: string; panelIds: string[] }): void {
    if (event.active) {
      this.flipActiveGridIds.add(event.gridId);
      for (const pid of event.panelIds) {
        this.flipActivePanels.add(pid);
      }
    } else {
      this.flipActiveGridIds.delete(event.gridId);
      for (const pid of event.panelIds) {
        this.flipActivePanels.delete(pid);
      }
    }

    this.panelsWithFlipActiveGrid.clear();
    const nodes = this.builderService.currentState.nodes;
    for (const gid of this.flipActiveGridIds) {
      const gNode = nodes.find(n => (n.props as any)?.field === gid);
      if (gNode?.parentId) {
        this.panelsWithFlipActiveGrid.add(gNode.parentId);
      }
    }

    this.cdr.markForCheck();
  }

  onDrawerStateChanged(event: { active: boolean; gridId: string; panelIds: string[]; drawerWidth: number }): void {
    if (!event || !event.panelIds) return;
    if (event.active) {
      for (const pid of event.panelIds) {
        this.drawerActivePanels.add(pid);
      }
      this.drawerPanelIds = Array.from(this.drawerActivePanels);
      this.drawerWidth = event.drawerWidth || 40;
      this.isDrawerVisible = true;
    } else {
      for (const pid of event.panelIds) {
        this.drawerActivePanels.delete(pid);
      }
      if (this.drawerActivePanels.size === 0) {
        this.isDrawerVisible = false;
        this.drawerPanelIds = [];
      } else {
        this.drawerPanelIds = Array.from(this.drawerActivePanels);
      }
    }
    this.cdr.markForCheck();
  }

  onDrawerClose(): void {
    this.isDrawerVisible = false;
    this.drawerActivePanels.clear();
    this.drawerPanelIds = [];
    this.cdr.markForCheck();
  }

  /**
   *  NOVO: Abre imagem em lightbox (modal)
   */
  onImageClick(node: BuilderNode, event: MouseEvent): void {
    event.stopPropagation(); // Não selecionar o componente
    
    const props = node.props as any;
    
    if (!props.enableLightbox) {
      return;
    }
    
    const imageUrl = props.imageUrl || 'https://via.placeholder.com/300x200';
    this.showLightbox(imageUrl, props.alt || 'Imagem');
  }

  /**
   *  NOVO: Exibe imagem em modal lightbox
   */
  private showLightbox(imageUrl: string, alt: string): void {
    // Criar overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      cursor: pointer;
      animation: fadeIn 0.2s ease;
    `;
    
    // Criar container da imagem
    const container = document.createElement('div');
    container.style.cssText = `
      position: relative;
      max-width: 90%;
      max-height: 90%;
      animation: zoomIn 0.3s ease;
    `;
    
    // Criar imagem
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = alt;
    img.style.cssText = `
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
      border-radius: 8px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    `;
    
    // Criar botão de fechar
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '✕';
    closeButton.style.cssText = `
      position: absolute;
      top: -15px;
      right: -15px;
      width: 40px;
      height: 40px;
      border: none;
      border-radius: 50%;
      background: white;
      color: #333;
      font-size: 24px;
      font-weight: bold;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      transition: all 0.2s ease;
      z-index: 10001;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    `;
    
    closeButton.onmouseover = () => {
      closeButton.style.background = '#ff4d4f';
      closeButton.style.color = 'white';
      closeButton.style.transform = 'scale(1.1)';
    };
    
    closeButton.onmouseout = () => {
      closeButton.style.background = 'white';
      closeButton.style.color = '#333';
      closeButton.style.transform = 'scale(1)';
    };
    
    // Adicionar animações CSS
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes zoomIn {
        from { transform: scale(0.8); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    // Montar estrutura
    container.appendChild(img);
    container.appendChild(closeButton);
    overlay.appendChild(container);
    document.body.appendChild(overlay);
    
    // Fechar ao clicar no overlay ou botão
    const closeModal = () => {
      overlay.style.animation = 'fadeIn 0.2s ease reverse';
      setTimeout(() => {
        document.body.removeChild(overlay);
        document.head.removeChild(style);
      }, 200);
    };
    
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });
    
    closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      closeModal();
    });
    
    // Fechar com ESC
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }

  /**
   *  NOVO: Executa eventos onInit de todos os componentes
   */
  private executeInitEvents(): void {
    if (this.viewMode !== 'view') {
      return; // Apenas executar em modo view (runtime)
    }

    this.builderService.nodes$.pipe(take(1)).subscribe(nodes => {
      nodes.forEach(node => {
        if ((node.props as any).events) {
          this.eventScriptService.executeInitEvents(node, this.getAllFormData());
        }
      });
    });
  }

  /**
   *  NOVO: Executa eventos onChange de um componente específico
   */
  private executeChangeEvents(node: BuilderNode, newValue: any, oldValue: any): void {
    if (this.viewMode !== 'view') {
      return; // Apenas executar em modo view (runtime)
    }

    if ((node.props as any).events) {
      this.eventScriptService.executeChangeEvents(
        node,
        newValue,
        oldValue,
        this.getAllFormData()
      );
      this.cdr.markForCheck();
    }
  }

  /**
   *  NOVO: Obtém todos os dados do formulário para contexto de eventos
   */
  private getAllFormData(): Record<string, any> {
    const formData: Record<string, any> = {};
    
    // Obter dados do DataBindingService (se disponível)
    try {
      // getData$ retorna Observable, então precisamos do último valor
      // Como estamos em contexto síncrono, vamos retornar objeto vazio por enquanto
      // Em produção, os eventos terão acesso aos dados através do contexto runtime
      this.dataBindingService.getData$().pipe(take(1)).subscribe(data => {
        if (data) {
          Object.assign(formData, data);
        }
      });
    } catch (e) {
      // Ignorar erro se não houver dados
    }

    return formData;
  }

  // ==========================================
  // FORMATAÇÃO DE NÚMERO (NG-ZORRO)
  // ==========================================

  /**
   * Retorna uma função formatter para o nz-input-number
   * Usado para formatar números com separadores de milhar/decimal
   * 
   * @param node - BuilderNode do componente number
   * @returns Função (value: number) => string
   */
  getNumberFormatter(node: BuilderNode): (value: number) => string {
    const props = node.props as any;
    const config: NumberFormatConfig = {
      decimalPlaces: props.decimalPlaces ?? 2,
      formatThousands: props.formatThousands ?? false,
      thousandSeparator: props.thousandSeparator ?? '.',
      decimalSeparator: props.decimalSeparator ?? ',',
    };
    return (value: number) => this.numberFormatterService.format(value, config);
  }

  /**
   * Retorna uma função parser para o nz-input-number.
   * Retorna STRING (não número) — necessário para que o nz-input-number
   * mantenha entradas parciais como "55." durante a digitação de decimais.
   * 
   * @param node - BuilderNode do componente number
   * @returns Função (value: string) => string
   */
  getNumberParser(node: BuilderNode): (value: string) => string {
    const props = node.props as any;
    const config: NumberFormatConfig = {
      thousandSeparator: props.thousandSeparator ?? '.',
      decimalSeparator: props.decimalSeparator ?? ',',
    };
    return (value: string) => this.numberFormatterService.parseForInput(value, config);
  }

  /**
   *  Cleanup ao destruir o component
   */
  ngOnDestroy(): void {
    this.masterDetailSubscription?.unsubscribe();
    this.dataBindingSubscription?.unsubscribe();
  }

}

